import PropTypes from 'prop-types';
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import _, { get } from 'lodash';

import { useSelector } from 'react-redux';
import { UI54Table } from '@agpl/ui54kit';
import dayjs from 'dayjs';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiContext } from '../../api/ApiContextProvider';
import { getAllGroups, getDefaultGroupNames, getDefaultGroupsMap } from '../selectors';
import TableTitleWrapper from '../../components/TableTitleWrapper';
import { capitalize } from '../../mainUtils';
import BaseTag from '../../_ui/BaseTag/BaseTag';
import { getNameForActor } from '../../54origins/utils54origins';
import InternalOrExternalTag from './InternalOrExternalTag';
import BaseButton from '../../_ui/BaseButton/BaseButton';
import useURLParams from '../../hooks/useURLParams';
import { getRegisteredOnServicesDataMap } from '../reducers/actorsMapReducer';
import RobotActorTag from './RobotActorTag';
import ActorsModalBtn from './ActorsModalBtn';
import { getLocale } from '../../locale/selectors';

function SelectedGroupsTable({
  selectedGroups,
  selectActor,
  actorUUID,
  selectedActors,
  onDeleteGroup,
  switchView,
}) {
  // console.log('RENDER')
  const {
    requestGetAllGroups,
  } = useContext(ApiContext);

  const { t } = useTranslation();

  const allGroups = useSelector(getAllGroups);
  const defaultGroupsMap = useSelector(getDefaultGroupsMap);

  const locale = useSelector(getLocale) || 'en';

  const registeredOnServicesData = useSelector(getRegisteredOnServicesDataMap);

  const defaultGroupNames = useSelector(getDefaultGroupNames);

  const { routeNavigateAndClearParams } = useURLParams();

  const [localActors, setLocalActors] = useState([]);

  const formatName = (cell, row) => {
    const isRoot = row.root;
    const isDefault = row.default;
    const tagName = isRoot ? 'root' : 'default';

    let tagNames = [];

    if (row.groups && row.groups.length > 0 && allGroups.length > 0) {
      tagNames = row.groups
        .map((uuid) => _.get(defaultGroupsMap.get(uuid), 'uinfo.group_name'))
        .filter((item) => ['BAN', 'ADMIN'].includes(item));
    }

    // console.log('ROW ROW', row)

    return (
      <div className="flex items-center break-word">
        {cell}
        {(isRoot || isDefault) && (
          <BaseTag className={`tag-${isRoot ? 'purple' : 'gray'} ml-2`}>
            {t(`auth.headers.${tagName}`, tagName).toUpperCase()}
          </BaseTag>
        )}
        {tagNames.map((tag) => (
          <BaseTag
            key={`default_tag_${row.uuid}_${tag}`}
            className={`tag-${tag === 'BAN' ? 'red' : 'volcano'} ml-2`}
          >
            {t(`auth.headers.${tag.toLowerCase()}`, tag)}
          </BaseTag>
        ))}
        <RobotActorTag isRobot={row?.isRobot} />
      </div>
    );
  };

  const formatType = (cell) => (
    <BaseTag className={`tag-${cell}`}>
      {capitalize(t(`auth.headers.${cell}`, cell))}
    </BaseTag>
  );

  const formatGroup = (cell, row) => {
    const { type, uuid } = row;

    return (
      <ActorsModalBtn
        actorUUID={uuid}
        actorType={type}
      />
    );
  };

  const formatWeight = (cell, row) => (row.type === 'group' ? (
    <Tooltip title={cell}>
      <span
        className="inline-block truncate"
        style={{ maxWidth: 75 }}
      >
        {cell > 4294967297 ? 'MAX' : cell}
      </span>
    </Tooltip>
  ) : null);

  const formatActions = (uuid) => (
    <div className="flex items-center h-full">
      <Tooltip
        title={capitalize('Remove from filters')}
      >
        <BaseButton
          className="button-danger-outlined px-2 ml-2"
          size="small"
          disabled={false}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteGroup(uuid);
          }}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <span style={{ padding: '0 1px' }}>
            X
          </span>
        </BaseButton>
      </Tooltip>
    </div>
  );

  const rowSelection = {
    columnWidth: 40,
    selectedRowKeys: [actorUUID],
  };

  const columns = useMemo(() => (
    [
      {},
      {
        dataIndex: 'name',
        key: 'name',
        className: 'p-2',
        title: (
          <TableTitleWrapper minWidth="100%">
            {capitalize(t('auth.headers.title', 'title'))}
          </TableTitleWrapper>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
        sortDirections: ['ascend', 'descend'],
        render: formatName,
      },
      {
        dataIndex: 'type',
        key: 'type',
        title: (
          <TableTitleWrapper minWidth={40}>
            {capitalize(t('auth.headers.type', 'type'))}
          </TableTitleWrapper>
        ),
        className: 'p-2',
        align: 'left',
        render: formatType,
      },
      {
        dataIndex: 'groups',
        key: 'groups',
        title: (
          <TableTitleWrapper minWidth={50}>
            {capitalize(t('auth.headers.groups', 'groups'))}
          </TableTitleWrapper>
        ),
        className: 'p-2',
        render: formatGroup,
      },
      {
        dataIndex: 'weight',
        key: 'weight',
        title: (
          <TableTitleWrapper minWidth={60}>
            {capitalize(t('auth.headers.weight', 'weight'))}
          </TableTitleWrapper>
        ),
        className: 'p-2',
        sorter: (a, b) => a.weight - b.weight,
        sortDirections: ['ascend', 'descend'],
        render: formatWeight,

      },
      {
        dataIndex: 'registeredOnServiceUUID',
        key: 'registered_on_service_uuid',
        title: (
          <TableTitleWrapper minWidth={100}>
            {capitalize(t('auth.headers.weight', 'registered on'))}
          </TableTitleWrapper>
        ),
        className: 'p-2',
        render: (cell) => (<BaseTag color="blue">{cell ? getNameForActor(registeredOnServicesData.get(cell)) : 'not found'}</BaseTag>),
      },
      {
        dataIndex: 'internal',
        key: 'internal',
        title: (
          <TableTitleWrapper minWidth={60}>
            {capitalize(t('auth.headers.weight', 'internal'))}
          </TableTitleWrapper>
        ),
        className: 'p-2',
        sorter: (a, b) => a.internal - b.internal,
        sortDirections: ['ascend', 'descend'],
        render: (cell) => (<InternalOrExternalTag internal={cell} />),
      },
      {
        dataIndex: 'created',
        key: 'created',
        title: (
          <TableTitleWrapper minWidth={120}>
            {capitalize(t('auth.headers.created', 'created'))}
          </TableTitleWrapper>
        ),
        className: 'p-2',
        textWrap: 'word-break',
        sorter: (a, b) => {
          const dateA = new Date(a.created).getTime();
          const dateB = new Date(b.created).getTime();
          return dateA - dateB;
        },
        sortDirections: ['ascend', 'descend'],
        render: (cell) => capitalize(dayjs.utc(cell)
          .local()
          .locale(locale)
          .format('MMM D, h:mm A')),
        defaultSortOrder: 'descend',
      },
      {
        dataIndex: 'uuid',
        key: 'actions',
        className: 'p-2 pr-4',
        title: '',
        render: formatActions,
      },
    ]
  ), [selectedActors, localActors, registeredOnServicesData]);

  const formattedTableData = (data) => data.map((item) => {
    const {
      actor_type: type,
      uuid,
      created,
      root_perms_signature: root,
    } = item;

    const getInfo = (path, value = '') => get(item, `uinfo.${path}`, value);

    const actorData = {
      uuid,
      type,
      created,
      root,
      weight: getInfo('weight', 1),
      comment: getInfo('comment'),
      internal: getInfo('internal_user', false),
    };

    switch (type) {
      case 'user':
      case 'classic_user':
        return {
          ...actorData,
          name: `${getInfo('first_name')} ${getInfo('last_name')}`,
          groups: getInfo('groups', []),
        };

      case 'group':
        const name = getInfo('group_name', '');

        return {
          ...actorData,
          name,
          default: defaultGroupNames.includes(name),
        };

      case 'service':
        return {
          ...actorData,
          name: getInfo('service_name'),
          groups: getInfo('groups', []),
        };

      default:
        return item;
    }
  });

  const onRow = (row) => {
    const { uuid } = row;

    return {
      onClick: (e) => {
        e.stopPropagation();
        if (actorUUID === uuid) {
          switchView?.();
        } else {
          selectActor('info', uuid);
        }
      },
      onDoubleClick: (e) => {
        e.stopPropagation();

        routeNavigateAndClearParams('/admin/actors/permissions', {
          uuid,
        });
      },
    };
  };

  const initFunc = async () => {
    const data = {
      uuid: selectedGroups,
    };

    const actorsData = await requestGetAllGroups(data, []);

    const { actors } = actorsData;

    setLocalActors(formattedTableData(actors));
  };

  useEffect(() => {
    initFunc();
  }, [selectedGroups]);

  return (
    <div>
      <h4>Selected group filters</h4>

      <UI54Table
        columns={columns}
        dataSource={localActors}
        loading={false}
        onRow={onRow}
        rowSelection={rowSelection}
        tableLayout="auto"
        useDefaultPagination
      />
    </div>
  );
}

export default SelectedGroupsTable;

SelectedGroupsTable.propTypes = {
  actorUUID: PropTypes.string,
  selectActor: PropTypes.func,
  selectedActors: PropTypes.array,
  selectedGroups: PropTypes.array,
  onDeleteGroup: PropTypes.func,
  switchView: PropTypes.func,
};
